import React from 'react';
import { cellContentStyle } from '@fabric-ui/theme';
import { Box, styled } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { AvHighlight } from '../AvHighlight';
import type { AvKeyValueProps } from './AvKeyValue';

const spaceForNiceLineBreaks = <>&#8203;</>;

const ROW_HEIGHT_PX = 27;

const StyledContainer = styled(Box)(({ theme }) => ({
  lineHeight: `${ROW_HEIGHT_PX}px`,
  fontSize: '13px',
  margin: 0,
  position: 'relative',

  '&.vertical': { span: { display: 'block', whiteSpace: 'normal' } },

  '& .AvKeyValueV2__row-container': {
    height: `${ROW_HEIGHT_PX}px`,
    position: 'absolute',
    borderRadius: '8px',
    paddingTop: '2px',
    paddingBottom: '2px',
    paddingLeft: '2px',
    transition: theme.transitions.create(['opacity', 'backgroundColor'], { duration: theme.transitions.duration.short }),
    ':hover': { backgroundColor: theme.palette.colors.neutrals[200] },
    ':hover .showInlineAction ': { opacity: 1 },
  },

  '& .showInlineAction': {
    position: 'absolute',
    paddingLeft: '16px',
    paddingRight: '16px',
    right: 0,
    top: 0,
    zIndex: 1,
    height: '100%',
    opacity: 0,
    gap: '16px',
    backgroundImage: `linear-gradient(to right, transparent, ${theme.palette.colors.neutrals[200]} 20%)`,
    transition: theme.transitions.create(['opacity'], { duration: theme.transitions.duration.short }),

    // Intended
    // eslint-disable-next-line fabric-ui/css-best-practices
    display: 'flex',
  },
  '&.vertical .showInlineAction': {
    // Intended
    // eslint-disable-next-line fabric-ui/css-best-practices
    display: 'flex',
  },

  dt: {
    backgroundColor: theme.palette.colors.primary[150],
    color: theme.palette.colors.neutrals[800],
    padding: '2px 6px',
    borderRadius: '8px',
    fontWeight: 500,
    '+ dd': { marginLeft: '4px', wordBreak: 'break-word' },
  },
  'dt, dd': { display: 'inline' },
}));

const horizontalStyle = (linesLimit: number) => ({
  ...cellContentStyle,
  WebkitLineClamp: linesLimit,
  wordBreak: 'break-word',
  '> span': { mr: 1 },
});

const Row = ({
  k,
  v,
  highlightText,
  inlineActions,
  ...props
}: { k: string; v: string } & Pick<AvKeyValueProps, 'highlightText' | 'inlineActions'> & React.ComponentProps<'span'>) => {
  return (
    <span className="AvKeyValueV2__row-container" {...props}>
      {spaceForNiceLineBreaks}
      <dt>{`${k}:`}</dt>
      <dd>
        <AvHighlight text={String(v)} highlights={highlightText} />
      </dd>

      {inlineActions && (
        <span className="showInlineAction">
          {inlineActions?.map(action => (
            <React.Fragment key={action.name}>{action(k, v)}</React.Fragment>
          ))}
        </span>
      )}
    </span>
  );
};

const MemoizedRow = React.memo(Row);

// TODO: Implement horizontal virtualization
const orientation = 'vertical' as AvKeyValueProps['orientation'];
type Props = Omit<AvKeyValueProps, 'orientation'>;

export const AvKeyValueV2 = ({ value = {}, highlightText, limit = 20, linesLimit = 4, inlineActions }: Props) => {
  const parentRef = React.useRef(null);

  const hozStyle = React.useMemo(() => (orientation === 'horizontal' ? horizontalStyle(linesLimit) : {}), [linesLimit, orientation]);
  const objectKeys = React.useMemo(() => Object.keys(value), [value]);
  const values = limit === -1 ? objectKeys : objectKeys.slice(0, limit);

  const rowVirtualizer = useVirtualizer({
    count: values.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT_PX,
  });

  return (
    <div
      ref={parentRef}
      style={{
        // @ts-expect-error: CSS variable
        '--total-size': rowVirtualizer.getTotalSize() + 'px',
        overflowY: 'auto',
        height: '100%',
      }}>
      <StyledContainer component="dl" className={orientation} sx={hozStyle} style={{ height: 'var(--total-size)' }}>
        {rowVirtualizer.getVirtualItems().map(item => {
          const key = values[item.index];
          const v = value[key];
          return (
            <MemoizedRow
              key={item.key}
              k={key}
              v={v}
              highlightText={highlightText}
              inlineActions={inlineActions}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${item.size}px`,
                transform: `translateY(${item.start}px)`,
              }}
            />
          );
        })}
      </StyledContainer>
    </div>
  );
};
